import Papa from 'papaparse';

export const SHOPIFY_CSV_MAX_BYTES = 15_000_000;
export const SHOPIFY_CSV_CHUNK_TARGET_BYTES = 14_000_000;
export const SORAFLO_IMPORT_CHUNK_TARGET_BYTES = 8_000_000;

export interface ShopifyCsvChunk {
  filename: string;
  text: string;
  bytes: number;
  rowCount: number;
  productCount: number;
}

function csvBytes(text: string): number {
  return new TextEncoder().encode(text).byteLength;
}

export function splitShopifyCsvByProduct(
  csvText: string,
  originalFilename: string,
  targetBytes = SHOPIFY_CSV_CHUNK_TARGET_BYTES,
): ShopifyCsvChunk[] {
  const parsed = Papa.parse<string[]>(csvText.replace(/^\uFEFF/, ''), {
    header: false,
    skipEmptyLines: 'greedy',
    dynamicTyping: false,
  });
  if (parsed.errors.length > 0 && parsed.data.length <= 1) {
    throw new Error(parsed.errors[0]?.message ?? 'Could not parse CSV');
  }

  const [header, ...rows] = parsed.data;
  if (!header?.length) throw new Error('CSV header row is missing');
  const handleIndex = header.findIndex((cell) => cell.trim().toLowerCase() === 'handle');
  if (handleIndex < 0) {
    throw new Error('A Shopify Handle column is required to split products safely');
  }

  const rowsByHandle = new Map<string, string[][]>();
  const productOrder: string[] = [];
  rows.forEach((row, index) => {
    const handle = String(row[handleIndex] ?? '').trim() || `__row_${index}`;
    if (!rowsByHandle.has(handle)) {
      rowsByHandle.set(handle, []);
      productOrder.push(handle);
    }
    rowsByHandle.get(handle)!.push(row);
  });

  const headerText = `\uFEFF${Papa.unparse([header])}\r\n`;
  const headerBytes = csvBytes(headerText);
  const grouped = productOrder.map((handle) => {
    const productRows = rowsByHandle.get(handle)!;
    const text = `${Papa.unparse(productRows)}\r\n`;
    return { text, bytes: csvBytes(text), rowCount: productRows.length };
  });

  const rawChunks: Array<{ parts: string[]; bytes: number; rows: number; products: number }> = [];
  let current = { parts: [] as string[], bytes: headerBytes, rows: 0, products: 0 };
  for (const group of grouped) {
    if (headerBytes + group.bytes > targetBytes) {
      throw new Error('One product is larger than Shopify’s CSV limit and cannot be split safely');
    }
    if (current.products > 0 && current.bytes + group.bytes > targetBytes) {
      rawChunks.push(current);
      current = { parts: [], bytes: headerBytes, rows: 0, products: 0 };
    }
    current.parts.push(group.text);
    current.bytes += group.bytes;
    current.rows += group.rowCount;
    current.products += 1;
  }
  if (current.products > 0) rawChunks.push(current);

  const baseName = originalFilename.replace(/\.csv$/i, '') || 'shopify-products';
  return rawChunks.map((chunk, index) => {
    const text = `${headerText}${chunk.parts.join('')}`;
    return {
      filename: `${baseName}-part-${index + 1}-of-${rawChunks.length}.csv`,
      text,
      bytes: csvBytes(text),
      rowCount: chunk.rows,
      productCount: chunk.products,
    };
  });
}